import {
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VAccordion,
  VAlert,
} from '@vrzl/ui';
import { Clock, Search, ShieldOff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useAuth } from '@/domains/auth';
import {
  CRITERIA_SEARCH_EMPTY_TITLE,
  CriteriaSearch,
  useCriteria,
  useCriteriaSearch,
} from '@/domains/criteria';
import { useDocuments } from '@/domains/documents';
import { EventHeader, useEvent } from '@/domains/events';
import { useCompletedExtraction, useValidationReport } from '@/domains/report';
import type { ValidationReportItem } from '@/domains/report/api/types';

import { computeValueDisplay } from '../utils/aggregateValue';
import {
  countByFilter,
  filterCriteria,
  type CriterionFilter,
} from '../utils/filterCriteria';
import { bucketByGroup, countGroup, hasConfiguredGroups } from '../utils/groupCriteria';
import { resolveOpenKeys } from '../utils/resolveOpenKeys';

import { CriterionGroup } from './criterionGroup';
import { InformationFilter } from './informationFilter';

const getName = (item: ValidationReportItem) => item.name;
const getValues = (item: ValidationReportItem) => [computeValueDisplay(item).value];

export function Information() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const activePerspective = user?.activePerspective ?? null;
  const activePerspectiveId = activePerspective?.id ?? null;

  const { data: event } = useEvent(eventId ?? '');
  const { data: documentsData } = useDocuments(eventId ?? '');
  const { data: criteriaData } = useCriteria(event?.applicationId ?? '', {
    perspectiveId: activePerspectiveId ?? undefined,
  });
  const { data: extractionData, isPending: isExtractionPending } = useCompletedExtraction(
    eventId ?? '',
  );

  const extractionId = extractionData?.items[0]?.id ?? null;
  const criterionIds = useMemo(
    () => (criteriaData?.items ?? []).map((c) => c.id),
    [criteriaData],
  );
  const validation = useValidationReport(extractionId, criterionIds);

  const [filter, setFilter] = useState<CriterionFilter>('all');

  const reportItems = useMemo(() => validation.data?.items ?? [], [validation.data]);
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: searchedItems,
    isSearching,
  } = useCriteriaSearch(reportItems, { getName, getValues });
  const filterCounts = useMemo(() => countByFilter(searchedItems), [searchedItems]);
  const visibleItems = useMemo(
    () => filterCriteria(searchedItems, filter),
    [searchedItems, filter],
  );
  const isNarrowed = filter !== 'all' || isSearching;

  // Counts always cover the whole group; rows only what filter + search leave.
  const groupViews = useMemo(() => {
    const visibleIds = new Set(visibleItems.map((item) => item.id));
    return bucketByGroup(reportItems)
      .map((bucket) => ({
        ...bucket,
        counts: countGroup(bucket.items),
        visibleItems: bucket.items.filter((item) => visibleIds.has(item.id)),
      }))
      .filter((view) => !isNarrowed || view.visibleItems.length > 0);
  }, [reportItems, visibleItems, isNarrowed]);

  // What the user toggled since the last filter or search change; null = use
  // the default. Reset on every change, so returning to an earlier filter or
  // search never brings back an old open state. Nothing is persisted.
  const [sentFeedback, setSentFeedback] = useState<ReadonlySet<string>>(new Set());
  const markFeedbackSent = (criterionId: string) =>
    setSentFeedback((prev) => new Set(prev).add(criterionId));

  const [manualOpenKeys, setManualOpenKeys] = useState<string[] | null>(null);
  const openKeys = resolveOpenKeys(
    manualOpenKeys,
    isNarrowed,
    groupViews.map((view) => view.key),
  );
  const handleFilterChange = (next: CriterionFilter) => {
    setFilter(next);
    setManualOpenKeys(null);
  };
  const handleSearchChange = (next: string) => {
    setSearchQuery(next);
    setManualOpenKeys(null);
  };

  if (!activePerspective) {
    return (
      <div>
        <PageHeader title="Informatie" description="Evenement informatie en context" />
        <EmptyState
          icon={ShieldOff}
          title="Geen perspectief toegewezen"
          description="Je hebt geen actief perspectief. Neem contact op met je beheerder."
        />
      </div>
    );
  }

  const documents = documentsData?.items ?? [];
  const isExtractionInFlight =
    event?.extractionStatus === 'QUEUED' || event?.extractionStatus === 'IN_PROGRESS';
  const hasExtractionForPerspective = extractionId !== null;
  const hasReport = validation.data !== undefined;

  return (
    <div>
      {event && <EventHeader event={event} documents={documents} />}

      {isExtractionPending && <LoadingState />}

      {!isExtractionPending && !hasExtractionForPerspective && (
        <EmptyState
          icon={Clock}
          title={
            isExtractionInFlight
              ? 'Extractie loopt nog'
              : !event?.extractionStatus
                ? 'Nog geen extractie voor dit evenement'
                : 'Geen extractie voor dit perspectief'
          }
          description="Resultaten verschijnen zodra de extractie voor dit perspectief beschikbaar is."
        />
      )}

      {hasExtractionForPerspective && validation.isPending && <LoadingState />}
      {hasExtractionForPerspective && validation.error && (
        <ErrorMessage error={validation.error} message="Kan informatie niet laden" />
      )}
      {hasReport && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <InformationFilter value={filter} counts={filterCounts} onChange={handleFilterChange} />
            <CriteriaSearch
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full max-w-xs"
            />
          </div>

          {reportItems.length > 0 && !hasConfiguredGroups(reportItems) && (
            <VAlert
              variant="info"
              className="mb-4"
              title="Voor dit perspectief zijn nog geen groepen ingericht."
            />
          )}

          {visibleItems.length > 0 ? (
            <VAccordion
              type="multiple"
              value={openKeys}
              onValueChange={setManualOpenKeys}
              className="space-y-3"
            >
              {groupViews.map((view) => (
                <CriterionGroup
                  key={view.key}
                  groupKey={view.key}
                  name={view.name}
                  counts={view.counts}
                  items={view.visibleItems}
                  eventId={eventId ?? ''}
                  sentFeedback={sentFeedback}
                  onFeedbackSent={markFeedbackSent}
                />
              ))}
            </VAccordion>
          ) : isSearching && searchedItems.length === 0 ? (
            <EmptyState
              icon={Search}
              title={CRITERIA_SEARCH_EMPTY_TITLE}
              description="Pas je zoekterm aan om andere criteria te zien."
            />
          ) : isSearching ? (
            <EmptyState
              icon={Search}
              title="Geen criteria voor dit filter"
              description="Geen van de zoekresultaten valt binnen dit filter. Kies 'Alles' om ze allemaal te zien."
            />
          ) : (
            <EmptyState
              icon={Search}
              title="Geen criteria voor dit filter"
              description="Er zijn geen criteria die aan dit filter voldoen. Kies 'Alles' om alle records weer te zien."
            />
          )}
        </>
      )}
    </div>
  );
}
